import { z } from "zod";
import { MechanicalNoteSchema } from "./prose.js";
import { FactIdSchema, ModifierValueSchema, MoveTypeSchema, PositionSchema, EffectSchema, DcSchema } from "./primitives.js";
/**
 * P2 Adjudicator output (docs/prompt_pack.md P2). Derived exactly from the
 * prompt's OUTPUT JSON and STEP 1–7 text, per ADR 0004/0005:
 *   - STEP 8 (intent scoring) is gone — see ADR 0005. There is no
 *     `intent_assessment` field.
 *   - The modifier cap is a NET-SUM bound, |Σ modifiers| <= 6 — see ADR 0005.
 *   - requires_roll is modeled as a discriminated union, not optional fields,
 *     so a no-roll CheckSpec cannot carry a dc or bands at the type level.
 */
export const ModifierSchema = z.strictObject({
    value: ModifierValueSchema,
    reason: MechanicalNoteSchema,
});
/** |Σ modifiers| <= 6 (ADR 0005). Modifiers may partially offset each other. */
const ModifiersArraySchema = z.array(ModifierSchema).superRefine((modifiers, ctx) => {
    const netSum = modifiers.reduce((total, modifier) => total + modifier.value, 0);
    if (Math.abs(netSum) > 6) {
        ctx.addIssue({
            code: "custom",
            message: `Net sum of modifiers must satisfy |sum of modifier.value| <= 6; got ${netSum}`,
        });
    }
});
/**
 * The closed consequence taxonomy (prompt_pack.md P2 STEP 6), each carrying
 * its own concrete payload. prompt_pack.md's worked example only shows
 * CLOCK_TICK, COMPLICATION, and RESOURCE_COST in full; the payload shapes for
 * REDUCED_EFFECT, LOST_OPPORTUNITY, ESCALATED_POSITION, and
 * NPC_DISPOSITION_SHIFT below are this package's own reasonable inference,
 * not a prompt_pack.md derivation — flagged in docs/RECONCILIATION.md.
 */
export const ConsequenceSchema = z.discriminatedUnion("type", [
    z.strictObject({ type: z.literal("REDUCED_EFFECT"), detail: MechanicalNoteSchema }),
    z.strictObject({ type: z.literal("COMPLICATION"), detail: MechanicalNoteSchema }),
    z.strictObject({ type: z.literal("LOST_OPPORTUNITY"), detail: MechanicalNoteSchema }),
    z.strictObject({
        type: z.literal("RESOURCE_COST"),
        resource: z.string().min(1),
        amount: z.number().int().positive(),
    }),
    z.strictObject({ type: z.literal("ESCALATED_POSITION"), new_position: PositionSchema }),
    z.strictObject({
        type: z.literal("CLOCK_TICK"),
        clock: z.string().min(1),
        amount: z.number().int().positive(),
    }),
    z.strictObject({
        type: z.literal("NPC_DISPOSITION_SHIFT"),
        npc: z.string().min(1),
        /** Always exactly one step (Disposition is an ordered enum) — there is no magnitude field to misuse. */
        direction: z.enum(["UP", "DOWN"]),
    }),
]);
export const BandOutcomeSchema = z.strictObject({
    /** Non-empty by construction (MechanicalNoteSchema enforces min length 1) — including for cost/miss, per the core-fact guarantee: even a miss always delivers something. */
    gain: MechanicalNoteSchema,
    consequences: z.array(ConsequenceSchema),
});
/** A success is never negated: REDUCED_EFFECT may not appear on the great or success bands. */
function rejectReducedEffectOnSuccess(band, ctx, path) {
    if (band.consequences.some((consequence) => consequence.type === "REDUCED_EFFECT")) {
        ctx.addIssue({
            code: "custom",
            message: `${path}.consequences may not contain REDUCED_EFFECT — a success is never negated`,
            path: [path, "consequences"],
        });
    }
}
export const CheckSpecBandsSchema = z
    .strictObject({
    great: BandOutcomeSchema,
    success: BandOutcomeSchema,
    cost: BandOutcomeSchema,
    miss: BandOutcomeSchema,
})
    .superRefine((bands, ctx) => {
    rejectReducedEffectOnSuccess(bands.great, ctx, "great");
    rejectReducedEffectOnSuccess(bands.success, ctx, "success");
});
/**
 * THE DEAL ON THE TABLE (pedagogy review, 2026-09-11; decision 2). Pre-declared
 * by the Adjudicator on a rolled check and PRESENTED only if the roll lands
 * COST and the turn delivered something less than clean: a named person
 * offers, in their own voice, to make the ask land in full — for a named,
 * immediate price. The learner takes it or leaves it in their next line, and
 * the reasons they give are what the Assessor reads for judgment,
 * calibration and communication.
 *
 * `cost_consequences` is the price in the engine's own vocabulary, applied
 * deterministically on acceptance; `offer` and `cost` are the words. The
 * benefit is fixed: what the turn delivered short is delivered clean.
 */
export const DevilsBargainSchema = z.strictObject({
    /** The NPC who makes the offer — a name from the case bible. */
    offered_by: z.string().min(1),
    offer: MechanicalNoteSchema,
    cost: MechanicalNoteSchema,
    cost_consequences: z.array(ConsequenceSchema).min(1).max(3),
});
const checkSpecCommonShape = {
    move_type: MoveTypeSchema,
    position: PositionSchema,
    position_reason: MechanicalNoteSchema,
    effect: EffectSchema,
    effect_reason: MechanicalNoteSchema,
    modifiers: ModifiersArraySchema,
    core_facts_guaranteed: z.array(FactIdSchema),
    /**
     * Historical (2026-09-17): the adjudicator no longer pre-declares bargains; they are authored
     * on the case's narrative arc. Kept optional so recorded specs still replay.
     */
    devils_bargain: DevilsBargainSchema.nullable().optional(),
};
const CheckSpecRequiresRollSchema = z.strictObject({
    requires_roll: z.literal(true),
    no_roll_reason: z.null(),
    ...checkSpecCommonShape,
    dc: DcSchema,
    bands: CheckSpecBandsSchema,
});
const CheckSpecNoRollSchema = z.strictObject({
    requires_roll: z.literal(false),
    no_roll_reason: MechanicalNoteSchema,
    ...checkSpecCommonShape,
});
/**
 * NOTE (docs/RECONCILIATION.md): the task instruction for this schema names
 * only `dc`/`bands` as required-absent when requires_roll is false. Whether
 * `modifiers` and `devils_bargain` still make sense without a roll to modify
 * is genuinely ambiguous in prompt_pack.md — P2 STEP 7 frames a devil's
 * bargain as a pre-ROLL offer ("+3 to the roll"), which has no obvious
 * meaning when there is no roll. This schema follows the literal instruction
 * (only dc/bands excluded) rather than inventing a further restriction; the
 * ambiguity is logged for you rather than silently resolved either way.
 */
export const CheckSpecSchema = z.discriminatedUnion("requires_roll", [
    CheckSpecRequiresRollSchema,
    CheckSpecNoRollSchema,
]);
/**
 * THE RETOOL ADJUDICATOR'S REPLY AS IT ARRIVES (Law 4; the stage-2 persona lanes, 2026-09-30).
 * Told the engine's "this turn does not roll", P2 1.10.0 answered `requires_roll: false` and still
 * priced a `dc` and `bands` in 9 of 115 calls; 3 more came as a JSON string and 1 wrapped alone in
 * `value`. 1.9.0 had none of these in 77. Code reads the reply before validating it:
 *
 *  - a JSON string holding an object, or an object whose only key is `value`, is unwrapped;
 *  - a no-roll reply's `dc` and `bands` are dropped: without a roll they price nothing, and the
 *    engine, not the model, decided the turn does not roll.
 *
 * Nothing is added, and anything else invalid stays invalid (an unknown key, a roll without
 * bands). Only the retool's P2 reads through this; 1.9.0 keeps the strict schema. The provider's
 * tool schema is the strict one: the pipe's output side.
 */
export function readAdjudicatorReply(raw) {
    let reply = raw;
    if (typeof reply === "string") {
        try {
            reply = JSON.parse(reply);
        }
        catch {
            return raw;
        }
    }
    if (!isPlainObject(reply))
        return raw;
    const keys = Object.keys(reply);
    if (keys.length === 1 && keys[0] === "value" && isPlainObject(reply["value"]))
        reply = reply["value"];
    if (isPlainObject(reply) && reply["requires_roll"] === false) {
        const { dc: _dc, bands: _bands, ...rest } = reply;
        return rest;
    }
    return reply;
}
export const CheckSpecIngressSchema = z.preprocess(readAdjudicatorReply, CheckSpecSchema);
function isPlainObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
//# sourceMappingURL=check-spec.js.map